/**
 * Logix Sync Manager
 * Handles synchronization of offline data with the server.
 * Batch size 10, exponential backoff (base 2s, max 5 retries, jitter).
 * Sync order: log entries -> checklist entries -> photos.
 */

import {
  getUnsyncedShiftLogEntries,
  getUnsyncedChecklistLogEntries,
  getUnsyncedPhotos,
  markShiftLogEntrySynced,
  markChecklistLogEntrySynced,
  markPhotoSynced,
  clearSyncedItems,
  getSyncStatusSummary,
  blobToBase64,
  metaGet
} from "./db"

const CONFIG = {
  API_BASE: "/api/v1/sync",
  MAX_RETRIES: 5,
  BASE_DELAY_MS: 2000,
  BATCH_SIZE: 10
}

let isSyncing = false
let syncListeners = []

export function onSyncUpdate(callback) {
  syncListeners.push(callback)
  return () => { syncListeners = syncListeners.filter((l) => l !== callback) }
}

function notifySyncUpdate(status) {
  syncListeners.forEach((listener) => {
    try { listener(status) } catch (e) { console.error("[Sync] Listener error:", e) }
  })
  // Also dispatch CustomEvent for Stimulus controllers
  document.dispatchEvent(new CustomEvent("sync:update", { detail: status }))
}

async function getAuthHeaders() {
  const token = await metaGet("jwt_token")
  const headers = {
    "Content-Type": "application/json",
    "Accept": "application/json"
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  } else {
    // Fallback: use CSRF token for session-based auth
    const csrfMeta = document.querySelector('meta[name="csrf-token"]')
    if (csrfMeta) {
      headers["X-CSRF-Token"] = csrfMeta.getAttribute("content")
    }
  }
  return headers
}

async function apiRequest(endpoint, data) {
  const headers = await getAuthHeaders()
  const response = await fetch(`${CONFIG.API_BASE}${endpoint}`, {
    method: "POST",
    headers,
    credentials: "same-origin",
    body: JSON.stringify(data)
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`API error: ${response.status} - ${error}`)
  }

  return response.json()
}

async function retryWithBackoff(fn, maxRetries = CONFIG.MAX_RETRIES, baseDelay = CONFIG.BASE_DELAY_MS) {
  let lastError
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error
      if (attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  }
  throw lastError
}

// ===== SYNC SHIFT LOG ENTRIES =====

export async function syncShiftLogEntries() {
  const entries = await getUnsyncedShiftLogEntries()
  if (entries.length === 0) return { synced: 0, failed: 0 }

  const results = { synced: 0, failed: 0, errors: [] }

  for (let i = 0; i < entries.length; i += CONFIG.BATCH_SIZE) {
    const batch = entries.slice(i, i + CONFIG.BATCH_SIZE)

    try {
      const response = await retryWithBackoff(() =>
        apiRequest("/log_entries", { entries: batch })
      )

      for (const result of (response.results || [])) {
        if (result.success) {
          await markShiftLogEntrySynced(result.client_uuid)
          results.synced++
        } else {
          results.failed++
          results.errors.push({ client_uuid: result.client_uuid, error: result.error })
        }
      }
    } catch (error) {
      // Batch failed — fallback to individual
      for (const entry of batch) {
        try {
          const response = await retryWithBackoff(() =>
            apiRequest("/log_entries", { entries: [entry] })
          )
          if (response.results?.[0]?.success) {
            await markShiftLogEntrySynced(entry.client_uuid)
            results.synced++
          } else {
            results.failed++
            results.errors.push({ client_uuid: entry.client_uuid, error: response.results?.[0]?.error })
          }
        } catch (itemError) {
          results.failed++
          results.errors.push({ client_uuid: entry.client_uuid, error: itemError.message })
        }
      }
    }
  }

  return results
}

// ===== SYNC CHECKLIST LOG ENTRIES =====

export async function syncChecklistLogEntries() {
  const entries = await getUnsyncedChecklistLogEntries()
  if (entries.length === 0) return { synced: 0, failed: 0 }

  const results = { synced: 0, failed: 0, errors: [] }

  for (let i = 0; i < entries.length; i += CONFIG.BATCH_SIZE) {
    const batch = entries.slice(i, i + CONFIG.BATCH_SIZE)

    try {
      const response = await retryWithBackoff(() =>
        apiRequest("/checklist_log_entries", { entries: batch })
      )

      for (const result of (response.results || [])) {
        if (result.success) {
          await markChecklistLogEntrySynced(result.client_uuid)
          results.synced++
        } else {
          results.failed++
          results.errors.push({ client_uuid: result.client_uuid, error: result.error })
        }
      }
    } catch (error) {
      for (const entry of batch) {
        try {
          const response = await retryWithBackoff(() =>
            apiRequest("/checklist_log_entries", { entries: [entry] })
          )
          if (response.results?.[0]?.success) {
            await markChecklistLogEntrySynced(entry.client_uuid)
            results.synced++
          } else {
            results.failed++
            results.errors.push({ client_uuid: entry.client_uuid, error: response.results?.[0]?.error })
          }
        } catch (itemError) {
          results.failed++
          results.errors.push({ client_uuid: entry.client_uuid, error: itemError.message })
        }
      }
    }
  }

  return results
}

// ===== SYNC PHOTOS =====

export async function syncPhotos() {
  const photos = await getUnsyncedPhotos()
  if (photos.length === 0) return { synced: 0, failed: 0 }

  const results = { synced: 0, failed: 0, errors: [] }

  for (const photo of photos) {
    try {
      const imageData = photo.blob ? await blobToBase64(photo.blob) : null
      if (!imageData) {
        results.failed++
        results.errors.push({ client_uuid: photo.client_uuid, error: "No image data" })
        continue
      }

      const response = await retryWithBackoff(() =>
        apiRequest("/photos", {
          entries: [{
            client_uuid: photo.client_uuid,
            parent_uuid: photo.parent_uuid,
            parent_type: photo.parent_type,
            image_data: imageData,
            filename: photo.filename,
            content_type: photo.content_type,
            photo_type: photo.photo_type,
            position: photo.position
          }]
        })
      )

      if (response.results?.[0]?.success) {
        await markPhotoSynced(photo.client_uuid)
        results.synced++
      } else {
        results.failed++
        results.errors.push({ client_uuid: photo.client_uuid, error: response.results?.[0]?.error })
      }
    } catch (error) {
      results.failed++
      results.errors.push({ client_uuid: photo.client_uuid, error: error.message })
    }
  }

  return results
}

// ===== SYNC ALL =====

export async function syncAll() {
  if (isSyncing) return { skipped: true }
  if (!navigator.onLine) return { offline: true }

  isSyncing = true
  notifySyncUpdate({ status: "syncing" })

  try {
    const shiftResults = await syncShiftLogEntries()
    const checklistResults = await syncChecklistLogEntries()
    const photoResults = await syncPhotos()
    await clearSyncedItems()

    const summary = await getSyncStatusSummary()
    const totalSynced = shiftResults.synced + checklistResults.synced + photoResults.synced

    const result = {
      success: true,
      shiftLogEntries: shiftResults,
      checklistLogEntries: checklistResults,
      photos: photoResults,
      remaining: summary
    }

    notifySyncUpdate({ status: "complete", synced: totalSynced, ...result })
    // Also dispatch for Stimulus
    document.dispatchEvent(new CustomEvent("sync:complete", { detail: { synced: totalSynced } }))

    return result
  } catch (error) {
    notifySyncUpdate({ status: "error", error: error.message })
    document.dispatchEvent(new CustomEvent("sync:error", { detail: { error: error.message } }))
    return { success: false, error: error.message }
  } finally {
    isSyncing = false
  }
}

// ===== BACKGROUND SYNC =====

export async function requestBackgroundSync(tag = "sync-all") {
  if (!("serviceWorker" in navigator) || !("SyncManager" in window)) return false

  try {
    const registration = await navigator.serviceWorker.ready
    await registration.sync.register(tag)
    return true
  } catch {
    return false
  }
}

export async function triggerManualSync() {
  const bgSyncRequested = await requestBackgroundSync()
  if (!bgSyncRequested) return syncAll()
  return { backgroundSyncRequested: true }
}

export async function getSyncStatus() {
  const summary = await getSyncStatusSummary()
  return { isSyncing, isOnline: navigator.onLine, ...summary }
}

export function isSyncInProgress() {
  return isSyncing
}
